/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState } from 'react'
import { LogInIcon, LogOutIcon, PlayIcon, RefreshCwIcon, SettingsIcon, ShieldCheckIcon, SlidersHorizontalIcon, UnplugIcon } from 'lucide-react'
import { logoutGuestSession } from '../auth-session'
import { CONNECTION } from '../screen-data'
import { COLORS, GLASS, ICON_SIZES, RADII, SHADOWS, TYPE, space } from '../theme'
import { AccountLink } from './AccountLink'
import { GuestSecurityScreen } from './GuestSecurityScreen'
import { SettingsScreen } from './SettingsScreen'

// Вёрстка — MainMenu v2 дизайнера (ревизия №9, задача B): полноэкранное меню
// поверх живого мира, честная статус-пилюля, вход только по реальной
// готовности, пауза в той же оболочке. Логика (фокус, геймпад, confirm'ы,
// роуты) — прежняя; данные не выдумываем.
// Кто уже вошёл и чем можно войти. Мир закрыт до входа (решение владельца
// 2026-08-05), поэтому меню обязано либо пустить, либо показать способ войти.
// Пока ответ сервера не пришёл — держим состояние 'unknown' и не мигаем
// формой входа тем, кто уже авторизован.
function useAccountGate() {
  const [gate, setGate] = useState({ status: 'unknown', providers: [], identities: [] })
  useEffect(() => {
    let alive = true
    const load = async () => {
      const read = async (url, options) => {
        try {
          const response = await fetch(url, options)
          if (!response.ok) return null
          return await response.json()
        } catch {
          return null
        }
      }
      const [providersData, identitiesData] = await Promise.all([
        read('/api/auth/providers'),
        read('/api/auth/identities', { credentials: 'include' }),
      ])
      if (!alive) return
      const providers = Array.isArray(providersData?.providers) ? providersData.providers : []
      const identities = Array.isArray(identitiesData?.identities) ? identitiesData.identities : []
      setGate({ status: identities.length ? 'in' : 'out', providers, identities })
    }
    load()
    return () => { alive = false }
  }, [])
  return gate
}

const PROVIDER_LABELS = { google: 'Google', github: 'GitHub' }

export function SessionShell({ world, state }) {
  const root = useRef(null)
  const previousFocus = useRef(null)
  const gamepadFrame = useRef(null)
  const gamepadPressed = useRef(false)
  const gamepadNav = useRef(0)
  const visible = Boolean(state?.visible)
  const gate = useAccountGate()

  useEffect(() => {
    if (!visible) return
    previousFocus.current = document.activeElement
    root.current?.querySelector('button:not(:disabled)')?.focus()
    return () => {
      if (previousFocus.current?.isConnected) previousFocus.current.focus()
    }
  }, [visible, state?.mode, state?.route])

  useEffect(() => {
    if (!visible || !globalThis.navigator?.getGamepads) return
    const poll = () => {
      const pad = [...(navigator.getGamepads() || [])].find(Boolean)
      const pressed = Boolean(pad?.buttons?.[0]?.pressed)
      if (pressed && !gamepadPressed.current && !document.querySelector('[role="alertdialog"][aria-modal="true"]')) document.activeElement?.click?.()
      gamepadPressed.current = pressed
      const direction = pad?.buttons?.[12]?.pressed || pad?.buttons?.[14]?.pressed || (pad?.axes?.[1] ?? 0) < -.6 ? -1 : pad?.buttons?.[13]?.pressed || pad?.buttons?.[15]?.pressed || (pad?.axes?.[1] ?? 0) > .6 ? 1 : 0
      if (direction && direction !== gamepadNav.current && !document.querySelector('[role="alertdialog"][aria-modal="true"]')) {
        const focusable = [...(root.current?.querySelectorAll('button:not(:disabled)') || [])]
        const index = focusable.indexOf(document.activeElement)
        focusable[(index + direction + focusable.length) % focusable.length]?.focus()
      }
      gamepadNav.current = direction
      gamepadFrame.current = requestAnimationFrame(poll)
    }
    gamepadFrame.current = requestAnimationFrame(poll)
    return () => cancelAnimationFrame(gamepadFrame.current)
  }, [visible])

  if (!visible) return null
  const closeRoute = () => world.ui.closeSessionRoute()
  if (state.route === 'settings' || state.route === 'accessibility') return <SettingsScreen world={world} initialTab={state.route === 'accessibility' ? 'accessibility' : 'graphics'} onClose={closeRoute} />
  if (state.route === 'security') return <GuestSecurityScreen world={world} onClose={closeRoute} />

  const disconnect = async () => {
    const confirmed = await world.ui.confirm({ kind: 'connection', tone: 'warning', safeEnter: false, title: 'ОТКЛЮЧИТЬСЯ ОТ МИРА?', message: 'Персонаж и настройки сохранятся. Это разорвёт текущую сетевую сессию; локальная пауза серверное время не останавливает.', dedupeKey: 'session-shell:disconnect' })
    if (!confirmed || !world.ui.startSessionAction('disconnect')) return
    world.network.disconnectToStart()
    world.ui.finishSessionAction('disconnect', { toMain: true })
  }
  const logout = async () => {
    const confirmed = await world.ui.confirm({ kind: 'security', tone: 'danger', safeEnter: false, title: 'ВЫЙТИ ИЗ ГОСТЕВОЙ СЕССИИ?', message: 'Сначала сохрани код восстановления. Сервер отзовёт HttpOnly-сессию, а клиент очистит только состояние текущего подключения.', dedupeKey: 'session-shell:logout' })
    if (!confirmed || !world.ui.startSessionAction('logout')) return
    try {
      await logoutGuestSession()
      world.network.clearOwnedSessionRuntime()
      world.ui.finishSessionAction('logout', { toMain: true })
      world.ui.notify({ kind: 'security', tone: 'success', title: 'СЕССИЯ ЗАВЕРШЕНА', message: 'Сервер отозвал гостевую сессию. При новом входе будет создан новый гость; сохранённый recovery-код остаётся способом вернуть прежнего персонажа.', dedupeKey: 'session-shell:logged-out', durable: true })
    } catch (error) {
      world.ui.finishSessionAction('logout')
      world.ui.notify({ kind: 'error', title: 'ВЫХОД НЕ ПОДТВЕРЖДЁН', message: error.code === 'RATE_LIMITED' ? 'Слишком много запросов. Повтори позже.' : 'Сервер не подтвердил отзыв сессии; подключение сохранено.', dedupeKey: 'session-shell:logout-failed' })
    }
  }
  const onKeyDown = event => {
    if (event.key !== 'Tab' || document.querySelector('[role="alertdialog"][aria-modal="true"]')) return
    const focusable = [...root.current.querySelectorAll('button:not(:disabled)')]
    if (!focusable.length) return
    const index = focusable.indexOf(document.activeElement)
    const next = event.shiftKey ? (index <= 0 ? focusable.length - 1 : index - 1) : (index >= focusable.length - 1 ? 0 : index + 1)
    event.preventDefault(); focusable[next].focus()
  }
  const main = state.mode === 'main'
  // Пока человек не вошёл в аккаунт, сервер осознанно не выдаёт билет в мир.
  // Показывать это красной «ошибкой соединения» — врать: сеть в порядке,
  // не хватает входа.
  const status = main && gate.status === 'out' ? 'login' : state.connection.status
  const connectionLabel = status === 'login' ? 'НУЖЕН ВХОД'
    : status === 'ready' ? 'МИР ГОТОВ'
    : status === 'reconnecting' ? 'ВОССТАНОВЛЕНИЕ СВЯЗИ'
    : status === 'error' ? 'ОШИБКА СОЕДИНЕНИЯ'
    : status === 'cancelled' ? 'ОФЛАЙН'
    : 'ПОДКЛЮЧЕНИЕ'

  return <section ref={root} onKeyDown={onKeyDown} role='dialog' aria-modal='true' aria-label={main ? 'Главное меню TESTCS World' : 'Локальное меню паузы TESTCS World'} css={shellCss(main)}>
    <header css={topbarCss}>
      <span css={wordmarkCss}>TESTCS</span>
      <span css={sparkCss} />
      <span css={wordmarkSubCss}>World</span>
      <span css={earlyBadgeCss}>РАННЯЯ ВЕРСИЯ</span>
      <span css={escPillCss}>ESC: {main ? 'недоступен до входа' : 'продолжить'}</span>
    </header>

    <div css={mainRowCss}>
      <main css={heroCss}>
        <div role='status' css={statusPillCss(status)}><i css={lampCss(status)} />{connectionLabel}</div>
        <div css={titleBlockCss}>
          <span css={bigTitleCss}>TESTCS</span>
          <span css={taglineCss}>Онлайн-мир на островах</span>
          {/* Список островов — по факту мира: он пересобран с нуля 2026-08-04,
              врать про снесённые локации в главном меню нельзя. */}
          <span css={subtitleCss}>Прибытие · Торговый круг · Тихая роща · Полигон — гуляйте, стройте, зовите друзей</span>
        </div>
        {main ? <>
          {/* Мир закрыт до входа в аккаунт. Пока сервер не ответил, кто мы,
              не показываем ни кнопку в мир, ни форму входа — иначе интерфейс
              мигает на каждом заходе. */}
          {gate.status === 'out' ? (
            <div css={loginBoxCss}>
              <strong>ВХОД В АККАУНТ</strong>
              <p>Персонаж, вещи и участок останутся вашими — и найдутся с любого устройства.</p>
              {gate.providers.length ? (
                <div css={loginRowCss}>
                  {gate.providers.map(provider => (
                    <a key={provider.id} href={`/api/auth/oauth/${provider.id}/start`} css={loginButtonCss}>
                      <LogInIcon size={ICON_SIZES.sm} />
                      ВОЙТИ ЧЕРЕЗ {(PROVIDER_LABELS[provider.id] || provider.label || provider.id).toUpperCase()}
                    </a>
                  ))}
                </div>
              ) : (
                <div css={noticeCss}>Способы входа сейчас не настроены на сервере. Мир откроется, как только они появятся.</div>
              )}
            </div>
          ) : (
            <>
              <button type='button' disabled={!state.canEnter || gate.status === 'unknown'} onClick={() => world.ui.enterWorld()} css={ctaCss}><PlayIcon size={ICON_SIZES.sm} />ВОЙТИ В МИР</button>
              {state.canRetry && <button type='button' onClick={() => CONNECTION.retry(world)} css={retryCss}><RefreshCwIcon size={ICON_SIZES.sm} />ПОВТОРИТЬ ПОДКЛЮЧЕНИЕ</button>}
              {!state.canEnter && !state.canRetry && <div css={noticeCss}>Мир готовится: соединение, ресурсы и твой персонаж. Прогресс не симулируется.</div>}
              {/* Привязка второго провайдера — только когда вход уже есть. */}
              <AccountLink />
            </>
          )}
        </> : <button type='button' disabled={!state.canResume} onClick={() => world.ui.resumeWorld()} css={ctaCss}><PlayIcon size={ICON_SIZES.sm} />ПРОДОЛЖИТЬ</button>}
      </main>
      <nav aria-label='Действия сессии' css={navCss}>
        <button type='button' onClick={() => world.ui.openSessionRoute('settings')}><SettingsIcon /> <span>НАСТРОЙКИ<small>Графика, звук и интерфейс</small></span></button>
        <button type='button' onClick={() => world.ui.openSessionRoute('accessibility')}><SlidersHorizontalIcon /> <span>ДОСТУПНОСТЬ<small>Масштаб, контраст, субтитры</small></span></button>
        <button type='button' onClick={() => world.ui.openSessionRoute('security')}><ShieldCheckIcon /> <span>БЕЗОПАСНОСТЬ<small>Гостевой вход и восстановление</small></span></button>
        {!main && state.canDisconnect && <button type='button' onClick={disconnect}><UnplugIcon /> <span>ОТКЛЮЧИТЬСЯ<small>Разорвать сеть, сохранить гостя</small></span></button>}
        <button type='button' disabled={Boolean(state.busy)} onClick={logout}><LogOutIcon /> <span>{state.busy === 'logout' ? 'ЗАВЕРШАЕМ…' : 'ВЫЙТИ ИЗ СЕССИИ'}<small>Выход на этом устройстве</small></span></button>
      </nav>
    </div>

    <footer css={footerCss}><span>Ранняя версия: сохранения профиля и облако ещё в работе</span><span css={bgCaptionCss}>{main ? 'фон: живой мир — вид со спавна' : 'локальная пауза: мир и серверное время идут'}</span></footer>
  </section>
}

// Полноэкранная оболочка поверх живого мира: виньетка по краям вместо глухой
// заливки; в паузе затемнение сильнее, чтобы читались подтверждения.
const shellCss = main => css`
  position:absolute;inset:0;z-index:9000;pointer-events:auto;color:${COLORS.text};
  display:grid;grid-template-rows:auto 1fr auto;gap:clamp(14px,2vmin,28px);
  padding:clamp(24px,3.4vmin,56px);overflow:auto;
  background:radial-gradient(130% 100% at 28% 72%,transparent 38%,rgba(3,7,13,${main ? '.82' : '.9'}) 100%);
  @keyframes sessionLamp{0%,100%{opacity:1}50%{opacity:.35}}
`
const topbarCss = css`display:flex;align-items:center;gap:${space(2)};flex-wrap:wrap;`
const wordmarkCss = css`font-family:'Chakra Petch','IBM Plex Sans',sans-serif;font-weight:600;font-size:17px;letter-spacing:.16em;text-transform:uppercase;`
const sparkCss = css`width:5px;height:5px;border-radius:${RADII.pill};background:${COLORS.cyan};box-shadow:0 0 8px ${COLORS.cyanSoft};`
const wordmarkSubCss = css`font-family:'Exo 2','IBM Plex Sans',sans-serif;font-weight:700;font-size:13px;letter-spacing:.3em;text-transform:uppercase;color:${COLORS.textSecondary};`
const earlyBadgeCss = css`font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:.14em;color:${COLORS.warning};border:1px solid ${COLORS.warningSoft};border-radius:${RADII.pill};padding:4px 11px;`
const escPillCss = css`margin-left:auto;font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.1em;color:${COLORS.textSecondary};background:${COLORS.bgPanel};border:1px solid ${COLORS.border};border-radius:${RADII.pill};padding:6px 13px;backdrop-filter:${GLASS.light};`
const mainRowCss = css`display:grid;grid-template-columns:1fr auto;gap:clamp(24px,4vmin,64px);align-items:end;min-height:0;@media(max-width:820px){grid-template-columns:1fr;align-items:start;}`
const heroCss = css`display:grid;gap:clamp(14px,2.2vmin,24px);align-content:end;justify-items:start;`
const statusPillCss = status => css`
  display:inline-flex;align-items:center;gap:12px;padding:10px 18px;border-radius:${RADII.pill};
  background:${status === 'ready' ? COLORS.successSoft : status === 'error' ? COLORS.errorSoft : COLORS.warningSoft};
  border:1px solid ${COLORS.border};backdrop-filter:${GLASS.light};
  font-family:'IBM Plex Mono',monospace;font-size:13px;font-weight:500;letter-spacing:.2em;
  color:${status === 'ready' ? COLORS.success : status === 'error' ? COLORS.error : COLORS.warning};
`
const lampCss = status => css`
  width:9px;height:9px;border-radius:${RADII.pill};
  background:${status === 'ready' ? COLORS.success : status === 'error' ? COLORS.error : COLORS.warning};
  box-shadow:0 0 12px ${status === 'ready' ? COLORS.successSoft : status === 'error' ? COLORS.errorSoft : COLORS.warningSoft};
  animation:${status === 'ready' ? 'none' : 'sessionLamp 1.6s ease-in-out infinite'};
`
const titleBlockCss = css`display:grid;gap:6px;`
const bigTitleCss = css`font-family:'Chakra Petch','IBM Plex Sans',sans-serif;font-weight:600;font-size:clamp(64px,9vmin,124px);line-height:.95;letter-spacing:.02em;text-transform:uppercase;text-shadow:0 0 72px ${COLORS.cyanSoft};`
const taglineCss = css`font-family:'Exo 2','IBM Plex Sans',sans-serif;font-weight:700;font-size:clamp(13px,1.4vmin,16px);letter-spacing:.34em;text-transform:uppercase;`
const subtitleCss = css`font-size:clamp(13px,1.35vmin,15px);color:${COLORS.textSecondary};`
const ctaCss = css`
  display:flex;align-items:center;justify-content:center;gap:${space(1)};
  width:min(400px,60vw);height:60px;border-radius:${RADII.lg};
  background:${COLORS.cyan};color:${COLORS.bgDeep};border:1px solid ${COLORS.cyanHover};
  font-family:'Exo 2','IBM Plex Sans',sans-serif;font-weight:800;font-size:17px;letter-spacing:.16em;text-transform:uppercase;cursor:pointer;
  box-shadow:${SHADOWS.glowCyan},0 16px 48px ${COLORS.cyanSoft};transition:background .12s ease-out;
  &:hover{background:${COLORS.cyanHover};}
  &:active{background:${COLORS.cyanPressed};}
  &:disabled{opacity:.42;cursor:not-allowed;box-shadow:none;}
`
const retryCss = css`
  display:flex;align-items:center;justify-content:center;gap:${space(1)};
  min-height:48px;padding:0 ${space(3)};border-radius:${RADII.lg};
  border:1px solid ${COLORS.borderStrong};background:${COLORS.bgElevated};color:${COLORS.text};
  font-weight:800;letter-spacing:.08em;cursor:pointer;
`
const noticeCss = css`padding:${space(2)};border:1px solid ${COLORS.border};border-radius:${RADII.lg};background:${COLORS.bgPanelLight};color:${COLORS.textSecondary};font-size:${TYPE.caption.size};backdrop-filter:${GLASS.light};`
const loginBoxCss = css`
  display:grid;gap:${space(2)};max-width:32rem;padding:${space(3)};
  border:1px solid ${COLORS.borderStrong};border-radius:${RADII.xxl};
  background:${COLORS.bgPanel};box-shadow:${SHADOWS.panel};backdrop-filter:${GLASS.light};
  strong{font-family:'Exo 2','IBM Plex Sans',sans-serif;font-size:${TYPE.title.size};letter-spacing:.18em;}
  p{margin:0;color:${COLORS.textSecondary};line-height:1.5;font-size:${TYPE.caption.size};}
`
const loginRowCss = css`display:flex;flex-wrap:wrap;gap:${space(1)};`
const loginButtonCss = css`
  display:inline-flex;align-items:center;justify-content:center;gap:${space(1)};
  min-height:52px;padding:0 ${space(3)};border-radius:${RADII.lg};
  background:${COLORS.cyan};color:${COLORS.bgDeep};border:1px solid ${COLORS.cyanHover};
  font-family:'Exo 2','IBM Plex Sans',sans-serif;font-weight:800;font-size:14px;letter-spacing:.12em;
  text-decoration:none;box-shadow:${SHADOWS.glowCyan};
  &:hover{background:${COLORS.cyanHover};}
  &:active{background:${COLORS.cyanPressed};}
`
const navCss = css`
  display:flex;flex-direction:column;gap:2px;align-self:end;min-width:300px;
  padding:${space(2)};border:1px solid ${COLORS.border};border-radius:${RADII.xxl};
  background:${COLORS.bgPanel};box-shadow:${SHADOWS.panel};backdrop-filter:${GLASS.light};
  button{min-height:58px;display:grid;grid-template-columns:28px 1fr;align-items:center;gap:${space(1)};padding:${space(1)} ${space(2)};border:1px solid transparent;border-radius:${RADII.lg};background:transparent;color:${COLORS.text};text-align:left;cursor:pointer;}
  button:hover,button:focus-visible{border-color:${COLORS.borderStrong};background:${COLORS.cyanSoft};}
  button:disabled{opacity:.42;cursor:not-allowed;}
  svg{width:${ICON_SIZES.sm}px;color:${COLORS.cyan};}
  span{display:grid;gap:3px;font-weight:700;letter-spacing:.06em;}
  small{color:${COLORS.textSecondary};font-size:${TYPE.caption.size};font-weight:400;letter-spacing:0;}
  @media(max-width:820px){align-self:stretch;min-width:0;}
`
const footerCss = css`display:flex;justify-content:space-between;gap:${space(2)};color:${COLORS.textSecondary};font-size:${TYPE.caption.size};@media(max-width:700px){flex-direction:column;}`
const bgCaptionCss = css`font-family:'IBM Plex Mono',monospace;letter-spacing:.12em;opacity:.75;`
